<template>
  <div class="library-container font-public-sans">
    <!-- Top Bar -->
    <header class="top-bar">
      <div class="flex items-center gap-3">
        <span class="text-[15px] font-bold tracking-tight text-[#1a1918]">Library</span>
        <span class="text-[12.5px] text-[#8b877f]">Module reference · checked nightly</span>
      </div>
      <div class="ml-auto flex items-center gap-4 text-[11.5px] text-[#56534d]">
        <span class="inline-flex items-center gap-1.5"><span class="w-[7px] h-[7px] rounded-full bg-[#2f8f5f]"></span>passed</span>
        <span class="inline-flex items-center gap-1.5"><span class="w-[7px] h-[7px] rounded-full bg-[#c23a28]"></span>failing</span>
        <span class="inline-flex items-center gap-1.5"><span class="w-[7px] h-[7px] rounded-full bg-[#64748b]"></span>unchecked</span>
        <span class="inline-flex items-center gap-1.5"><span class="w-[7px] h-[7px] rounded-full bg-[#dedad2]"></span>no tests</span>
      </div>
    </header>

    <!-- Run strip: what the checker is doing now, or what it last did. -->
    <div class="run-strip">
      <template v-if="activeRun">
        <span class="run-pulse"></span>
        <span class="font-semibold text-[#1a1918]">Checking {{ activeRun.done }} of {{ activeRun.test_ids.length }}</span>
        <span v-if="activeRun.current_phrase" class="min-w-0 truncate text-[#56534d]">{{ activeRun.current_phrase }}</span>
        <span v-else class="text-[#8b877f]">{{ activeRun.status === 'queued' ? 'waiting for the container' : 'between sentences' }}</span>
        <span class="ml-auto shrink-0 text-[#8b877f]">started {{ ago(activeRun.created_at) }} · {{ runWho(activeRun) }}</span>
        <span v-if="activeRun.stalled" class="shrink-0 text-[10.5px] font-semibold rounded-full px-2 py-0.5" :style="{ background: C.failBg, color: C.failInk }">no report for 15 min</span>
      </template>
      <template v-else-if="lastRun">
        <span class="w-[7px] h-[7px] rounded-full shrink-0" :style="{ background: runColor(lastRun) }"></span>
        <span class="text-[#1a1918]">Last run <span class="font-semibold">{{ runStatusWord(lastRun) }}</span> {{ ago(lastRun.finished_at || lastRun.updated_at) }}</span>
        <span class="text-[#56534d]">{{ runCountsText(lastRun) }}</span>
        <span class="ml-auto shrink-0 text-[#8b877f]">{{ runWho(lastRun) }}</span>
      </template>
      <span v-else class="text-[#8b877f]">No check runs recorded yet</span>
      <button type="button" class="shrink-0 ml-3 text-[11.5px] font-semibold text-[#35617f] hover:underline" @click="runsOpen = !runsOpen">{{ runsOpen ? 'Hide history' : 'History' }}</button>
    </div>

    <div v-if="runsOpen" class="run-history om-scroll">
      <p v-if="!runs.length" class="m-0 px-5 py-3 text-[12.5px] text-[#a8a49b] italic">No runs yet. The nightly run and every Re-check will appear here.</p>
      <table v-else class="run-table">
        <thead>
          <tr><th>Started</th><th>Source</th><th>By</th><th>Tests</th><th>Result</th><th>Took</th><th>Status</th></tr>
        </thead>
        <tbody>
          <template v-for="r in runs" :key="r.id">
            <tr class="run-row" :class="{ 'is-open': expandedRunId === r.id }" @click="expandedRunId = expandedRunId === r.id ? null : r.id">
              <td class="whitespace-nowrap">{{ stamp(r.created_at) }}</td>
              <td>{{ r.source }}</td>
              <td>{{ r.started_by_name || (r.source === 'schedule' ? 'nightly' : '—') }}</td>
              <td>{{ r.done }}/{{ r.test_ids.length }}</td>
              <td>{{ runCountsText(r) || '—' }}</td>
              <td>{{ runTook(r) }}</td>
              <td>
                <span class="text-[10.5px] font-medium rounded-full px-2 py-0.5" :style="{ background: runBg(r), color: runInk(r) }">{{ runStatusWord(r) }}</span>
                <span v-if="r.stalled" class="ml-1 text-[10.5px] text-[#993022]">stalled</span>
              </td>
            </tr>
            <tr v-if="expandedRunId === r.id" class="run-detail">
              <td colspan="7">
                <p v-if="r.error" class="m-0 mb-2 text-[12px] text-[#993022] whitespace-pre-wrap">{{ r.error }}</p>
                <p class="m-0 mb-2 text-[11.5px] text-[#8b877f]">
                  <span v-if="r.model">model {{ r.model }}</span>
                  <span v-if="r.task_id"> · task {{ r.task_id }}</span>
                  <span v-if="r.execution_id"> · execution {{ r.execution_id }}</span>
                </p>
                <div v-for="t in r.tests" :key="t.id" class="flex items-center gap-2.5 py-1 text-[12.5px]">
                  <span class="w-[7px] h-[7px] rounded-full shrink-0" :style="{ background: runTestColor(t) }"></span>
                  <a class="flex-1 min-w-0 truncate text-[#26241f] hover:underline cursor-pointer" @click.stop="goToPrereq(t.id)">{{ t.phrase || byId[t.id]?.phrase || t.id }}</a>
                  <span class="shrink-0 text-[#8b877f]">{{ t.result || t.state }}</span>
                  <span class="shrink-0 w-24 text-right text-[#a8a49b]">{{ t.finished_at ? stamp(t.finished_at) : (t.started_at ? stamp(t.started_at) : '') }}</span>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div class="main-body" :class="{ 'select-none': isDragging }">
      <!-- Left Index Nav -->
      <nav
        v-if="!sidebarCollapsed"
        class="sidebar om-scroll"
        :style="{ width: `${sidebarWidth}px` }"
      >
        <div class="sticky top-0 bg-[#fbfaf8] border-b border-[#eceae4] p-3 z-10">
          <div class="flex items-center gap-2">
            <input
              v-model.trim="query"
              type="search"
              placeholder="Filter modules…"
              class="w-full text-[12.5px] h-8 px-2.5 bg-white text-[#1a1918] border border-[#e0ddd6] rounded-md focus:outline-none focus:border-[#35617f]"
            />
            <button
              type="button"
              aria-label="Hide library index"
              title="Hide library index"
              class="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-md border border-[#e0ddd6] bg-white text-[#56534d] hover:border-[#b0aca3] hover:text-[#1a1918]"
              @click="toggleSidebar"
            >
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" class="text-xs" />
            </button>
          </div>
          <!-- CUSTOMER-FACING FILTER. The library documents both the product a
               customer touches and the machinery behind it; without this the two
               are indistinguishable in a 469-module tree. Counts are of the
               whole library, not the current search, so they read as "how much
               of the library is customer-facing" rather than moving with the
               query. -->
          <div class="flex items-center gap-1 mt-2.5">
            <button
              v-for="f in facingFilters"
              :key="f.key"
              type="button"
              class="flex-1 text-[11px] leading-none py-1.5 rounded-md border transition-colors"
              :class="facing === f.key
                ? 'bg-[#35617f] border-[#35617f] text-white font-semibold'
                : 'bg-white border-[#e0ddd6] text-[#56534d] hover:border-[#b0aca3]'"
              :title="f.hint"
              @click="facing = f.key"
            >{{ f.label }}<span class="ml-1 opacity-70">{{ f.count }}</span></button>
          </div>
          <div class="flex items-center justify-between mt-2.5 px-0.5">
            <span class="text-[12px] text-[#a8a49b]">{{ filteredNodes.length }} modules · {{ tests.length }} checks</span>
            <button
              type="button"
              class="text-[11.5px] font-semibold text-[#35617f] hover:underline"
              @click="startModule('')"
            >+ Module</button>
          </div>
        </div>

        <div class="p-1.5 pb-6">
          <div
            v-for="n in tree"
            :key="n.path"
            class="tree-row group"
            :class="{ 'selected': selectedPath === n.path }"
            @click="selectModule(n.path)"
          >
            <span :style="{ width: `${n.depth * 15}px` }" class="shrink-0"></span>
            <button
              v-if="n.hasChildren"
              type="button"
              class="w-4 h-4 inline-flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
              @click.stop="toggleExpand(n.path)"
            >
              <FontAwesomeIcon
                :icon="['fas', isExpanded(n.path) ? 'chevron-down' : 'chevron-right']"
                class="text-[9.5px]"
              />
            </button>
            <span v-else class="w-4 h-4 inline-block shrink-0"></span>
            <span class="w-[7px] h-[7px] rounded-full shrink-0" :style="{ background: n.aggColor }"></span>
            <span
              class="flex-1 min-w-0 truncate text-[13px]"
              :class="selectedPath === n.path ? 'font-semibold text-[#1a1918]' : (n.depth === 0 ? 'font-medium text-[#1a1918]' : 'text-[#56534d]')"
            >{{ n.title }}</span>
            <!-- The marker sits on the row itself, not only in the detail pane:
                 scanning the tree is how you answer "what do we document that a
                 customer actually sees". -->
            <span
              v-if="n.tenantFacing"
              class="shrink-0 text-[9px] font-bold tracking-wide px-1 py-px rounded bg-[#e7eef4] text-[#35617f]"
              title="Customer facing"
            >CF</span>
            <span v-if="n.testsCount" class="text-[10.5px] text-[#b0aca3] ml-1">{{ n.testsCount }}</span>
          </div>
        </div>
      </nav>

      <div v-if="sidebarCollapsed" class="sidebar-toggle-rail">
        <button
          type="button"
          aria-label="Show library index"
          title="Show library index"
          class="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#56534d] hover:bg-[#eef2f5] hover:text-[#1a1918]"
          @click="toggleSidebar"
        >
          <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-xs" />
        </button>
      </div>

      <!-- Draggable Splitter Bar -->
      <div
        v-else
        class="resizer-bar"
        :class="{ 'is-resizing': isDragging }"
        @mousedown.prevent="startResize"
      >
        <div class="resizer-handle"></div>
      </div>

      <!-- Main Document Pane -->
      <main class="content-pane om-scroll">
        <div v-if="loading && !nodes.length" class="p-8 space-y-3 animate-pulse">
          <div class="h-6 bg-gray-200 rounded w-1/3"></div>
          <div class="h-4 bg-gray-100 rounded w-1/2"></div>
          <div class="h-24 bg-gray-100 rounded mt-6"></div>
        </div>

        <div v-else-if="detailLoading && !detail" class="p-8 space-y-3 animate-pulse">
          <div class="h-6 bg-gray-200 rounded w-1/3"></div>
          <div class="h-4 bg-gray-100 rounded w-1/2"></div>
        </div>

        <div v-else-if="moduleForm" class="w-full px-8 py-6">
          <div class="flex items-center justify-between mb-4 pb-2 border-b border-[#e4e0d9]">
            <h2 class="text-lg font-bold text-[#1a1918]">{{ moduleForm.parent ? `New module under ${moduleTitle(moduleForm.parent)}` : 'New top-level module' }}</h2>
            <button type="button" class="text-gray-400 hover:text-gray-700" @click="moduleForm = null">✕</button>
          </div>
          <div class="space-y-4 max-w-xl">
            <label class="block"><span class="text-xs text-gray-500 font-medium">Title</span><input v-model="moduleForm.title" type="text" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm" /></label>
            <label class="block"><span class="text-xs text-gray-500 font-medium">Summary (60 words)</span><textarea v-model="moduleForm.summary" rows="3" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm" /></label>
            <label class="block"><span class="text-xs text-gray-500 font-medium">Read when (40 words)</span><textarea v-model="moduleForm.when_to_read" rows="2" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm" /></label>
            <p v-if="formError" class="text-sm text-red-600">{{ formError }}</p>
            <div class="flex items-center gap-3 pt-2">
              <button type="button" class="px-4 py-1.5 rounded bg-[#35617f] text-white font-medium text-xs hover:bg-[#2a4d65]" :disabled="saving" @click="saveModule">{{ saving ? 'Saving…' : 'Create' }}</button>
              <button type="button" class="text-xs text-gray-600 hover:underline" @click="moduleForm = null">Cancel</button>
            </div>
          </div>
        </div>

        <div v-else-if="detail" class="w-full px-8 py-6 pb-24">
          <!-- Breadcrumbs -->
          <div class="text-[11.5px] text-[#a8a49b] flex items-center gap-1.5 flex-wrap">
            <template v-for="(crumb, idx) in breadcrumbs" :key="crumb.path">
              <a
                class="cursor-pointer"
                :class="idx === breadcrumbs.length - 1 ? 'text-[#8b877f] pointer-events-none' : 'text-[#35617f] hover:underline'"
                @click="selectModule(crumb.path)"
              >{{ crumb.title }}</a>
              <span v-if="idx < breadcrumbs.length - 1" class="text-[#d8d4cc]">/</span>
            </template>
          </div>

          <!-- Title & Status -->
          <div class="flex items-start gap-3.5 mt-3">
            <h1 class="m-0 font-bold text-[28px] leading-tight tracking-tight text-[#1a1918] flex-1 min-w-0">{{ detail.title }}</h1>
            <span
              class="inline-flex items-center text-[10.5px] font-medium rounded-full px-2.5 py-1"
              :class="detail.tenant_facing ? 'bg-[#e7eef4] text-[#35617f]' : 'bg-[#f4f2ee] text-[#7a766d]'"
              :title="detail.tenant_facing
                ? 'Documents product a customer touches'
                : 'Internal machinery — no customer surface'"
            >{{ detail.tenant_facing ? 'Customer facing' : 'Internal' }}</span>
            <span
              class="inline-flex items-center text-[10.5px] font-medium rounded-full px-2.5 py-1"
              :class="detail.status === 'live' ? 'bg-[#e7f2ea] text-[#1f6a45]' : 'bg-[#efece6] text-[#7a766d]'"
            >{{ detail.status }}</span>
          </div>

          <!-- Metadata row -->
          <div class="flex items-center gap-3 mt-2.5 text-[12.5px] text-[#8b877f] flex-wrap">
            <span>Owner <span class="text-[#56534d] font-medium">{{ detailOwnerName }}</span></span>
            <span class="text-[#d8d4cc]">·</span>
            <span>Updated {{ ago(detail.updated_at) }}</span>
            <span class="text-[#d8d4cc]">·</span>
            <span class="font-mono text-xs">{{ detail.path }}</span>
            <span class="text-[#d8d4cc]">·</span>
            <button type="button" class="text-[#35617f] hover:underline" @click="startDetails">Edit</button>
            <span class="text-[#d8d4cc]">·</span>
            <button type="button" class="text-[#35617f] hover:underline" @click="startModule(detail.path)">+ child</button>
            <span class="text-[#d8d4cc]">·</span>
            <button type="button" :class="confirming ? 'text-red-600 font-bold' : 'text-gray-400 hover:text-red-600'" @click="removeModule">
              {{ confirming ? 'Confirm Delete?' : 'Delete' }}
            </button>
          </div>

          <!-- Health Strip Banner -->
          <div class="flex items-center gap-3 mt-4 p-3 bg-[#faf9f6] border border-[#eceae4] rounded-lg">
            <span class="w-[9px] h-[9px] rounded-full shrink-0" :style="{ background: moduleHealth.color }"></span>
            <span class="text-[13.5px]">
              <span class="font-bold" :style="{ color: moduleHealth.color }">{{ moduleHealth.score }}</span>
              <span class="text-[#56534d] ml-1.5">{{ moduleHealth.label }}</span>
            </span>
            <span class="text-[12.5px] text-[#a8a49b] ml-2">Last tested {{ lastTestedLabel }}</span>
            <button
              v-if="moduleDeepTests.length"
              type="button"
              class="ml-auto text-[12.5px] font-semibold text-[#1a1918] bg-white border border-[#e0ddd6] rounded-md h-[30px] px-3 hover:bg-gray-50"
              :disabled="moduleChecking"
              @click="runTests(moduleDeepTests)"
            >{{ moduleChecking ? 'Checking…' : 'Re-check all' }}</button>
          </div>

          <!-- Summary & Read When -->
          <p class="mt-5 text-[13.5px] leading-relaxed text-[#2c2a26]">{{ detail.summary }}</p>
          <p v-if="detail.when_to_read" class="mt-3 text-[12px] leading-normal text-[#6b675f] border-l-2 border-[#e4e0d9] pl-3">
            <span class="text-[11.5px] font-semibold text-[#8b877f]">Read when</span>&nbsp;&nbsp;{{ detail.when_to_read }}
          </p>

          <!-- Module Edit Form Inline -->
          <div v-if="detailsForm" class="mt-5 p-4 border border-[#e0ddd6] bg-[#faf9f6] rounded-lg">
            <h3 class="font-bold text-sm mb-3">Edit Module Details</h3>
            <div class="space-y-3">
              <label class="block"><span class="text-xs text-gray-500 font-medium">Title</span><input v-model="detailsForm.title" type="text" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm" /></label>
              <label class="block"><span class="text-xs text-gray-500 font-medium">Summary (60 words)</span><textarea v-model="detailsForm.summary" rows="3" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm" /></label>
              <label class="block"><span class="text-xs text-gray-500 font-medium">Read when (40 words)</span><textarea v-model="detailsForm.when_to_read" rows="2" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm" /></label>
              <div class="grid grid-cols-2 gap-3">
                <label class="block"><span class="text-xs text-gray-500 font-medium">Status</span>
                  <select v-model="detailsForm.status" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 bg-white text-sm">
                    <option value="live">live</option>
                    <option value="idle">idle</option>
                    <option value="dead">dead</option>
                  </select>
                </label>
                <label class="block"><span class="text-xs text-gray-500 font-medium">Parent (move)</span>
                  <input v-model="detailsForm.parent_path" type="text" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm" placeholder="blank = top level" />
                </label>
              </div>
              <label class="flex items-start gap-2 cursor-pointer">
                <input v-model="detailsForm.tenant_facing" type="checkbox" class="mt-0.5 w-3.5 h-3.5 accent-[#35617f]" />
                <span class="text-xs text-gray-600">
                  <span class="font-medium text-gray-700">Customer facing</span>
                  — documents product a customer touches, not internal machinery.
                </span>
              </label>
              <p v-if="formError" class="text-sm text-red-600">{{ formError }}</p>
              <div class="flex items-center gap-3 pt-2">
                <button type="button" class="px-4 py-1.5 rounded bg-[#35617f] text-white text-xs font-medium hover:bg-[#2a4d65]" :disabled="saving" @click="saveDetails">{{ saving ? 'Saving…' : 'Save' }}</button>
                <button type="button" class="text-xs text-gray-600 hover:underline" @click="detailsForm = null">Cancel</button>
              </div>
            </div>
          </div>

          <!-- Tabs Header -->
          <div class="flex items-center gap-1 mt-7 border-b border-[#e4e0d9]">
            <button
              v-for="t in tabList"
              :key="t.key"
              type="button"
              class="text-[12px] px-3 py-2 -mb-[1px] border-b-2 font-medium transition-colors"
              :class="tab === t.key ? 'text-[#1a1918] font-semibold border-[#35617f]' : 'text-[#8b877f] border-transparent hover:text-[#1a1918]'"
              @click="selectTab(t.key)"
            >
              {{ t.label }}
              <span v-if="t.count" class="ml-1.5 text-[10.5px]" :class="tab === t.key ? 'text-[#8b877f]' : 'text-[#b0aca3]'">{{ t.count }}</span>
            </button>
          </div>

          <!-- Tab: Overview / Body -->
          <div v-if="tab === 'overview'" class="pt-6">
            <div v-if="bodyForm" class="p-4 border border-[#e0ddd6] bg-[#faf9f6] rounded-lg">
              <textarea v-model="bodyForm.body" rows="18" class="w-full border border-gray-300 rounded p-2.5 font-mono text-xs leading-relaxed" />
              <label class="block mt-2"><span class="text-xs text-gray-500 font-medium">What changed and why (becomes history entry)</span><input v-model="bodyForm.note" type="text" class="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm" /></label>
              <p v-if="formError" class="text-sm text-red-600 mt-1">{{ formError }}</p>
              <div class="mt-3 flex items-center gap-3">
                <button type="button" class="px-4 py-1.5 rounded bg-[#35617f] text-white text-xs font-medium hover:bg-[#2a4d65]" :disabled="saving" @click="saveBody">{{ saving ? 'Saving…' : 'Save body' }}</button>
                <button type="button" class="text-xs text-gray-600 hover:underline" @click="bodyForm = null">Cancel</button>
              </div>
            </div>
            <div v-else>
              <div class="flex justify-end mb-3">
                <button type="button" class="text-[12.5px] font-semibold text-[#35617f] hover:underline" @click="startBody">{{ detail.body ? 'Edit Body' : '+ Write Body' }}</button>
              </div>
              <p v-if="!sections.length" class="m-0 text-[13px] text-[#a8a49b] italic">Nothing written yet.</p>
              <div v-else class="space-y-5">
                <section v-for="(s, i) in sections" :key="i">
                  <h2 v-if="s.heading" class="m-0 mb-1.5 font-semibold text-[13px] uppercase tracking-wide text-[#6b675f]">{{ s.heading }}</h2>
                  <template v-for="(b, j) in s.blocks" :key="j">
                    <ul v-if="b.type === 'list'" class="m-0 mt-1.5 pl-4 list-disc space-y-1">
                      <li v-for="(item, k) in b.items" :key="k" class="text-[13px] leading-relaxed text-[#38352f]">{{ item }}</li>
                    </ul>
                    <p v-else class="m-0 mt-1.5 text-[13px] leading-relaxed text-[#38352f] whitespace-pre-wrap">{{ b.text }}</p>
                  </template>
                </section>
              </div>
            </div>
          </div>

          <!-- Tab: Checks -->
          <div v-else-if="tab === 'checks'" class="pt-6">
            <div class="flex items-center gap-2 mb-2">
              <span class="text-[13px] text-[#8b877f]">{{ checkSummaryText }}</span>
              <button
                type="button"
                class="ml-auto text-[12.5px] font-semibold text-[#35617f] hover:underline"
                @click="startCreateTest(detail.path)"
              >+ Check</button>
            </div>

            <!-- Create / Edit Check Form -->
            <div v-if="testForm" class="my-4 p-4 border border-[#e0ddd6] bg-[#faf9f6] rounded-lg">
              <h3 class="font-bold text-sm mb-3">{{ testForm.id ? 'Edit Check' : 'New Check' }}</h3>
              <div class="space-y-3">
                <label class="block"><span class="text-xs text-gray-500 font-medium">Must be true (the sentence)</span><input v-model="testForm.phrase" type="text" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm" /></label>
                <div class="block">
                  <span class="text-xs text-gray-500 font-medium">True only if these are true</span>
                  <ul v-if="testForm.needs.length" class="mt-1 space-y-1">
                    <li v-for="n in testForm.needs" :key="n.id || n.phrase" class="flex items-center gap-2 text-xs text-gray-800">
                      <span class="flex-1">{{ n.phrase }}</span>
                      <button type="button" class="text-gray-400 hover:text-red-600" @click="removeNeed(n)">✕</button>
                    </li>
                  </ul>
                  <div class="mt-2 flex items-center gap-2">
                    <input v-model.trim="newNeed" type="text" placeholder="Add prerequisite sentence…" class="flex-1 border border-gray-300 rounded px-2.5 py-1 text-xs" />
                    <button type="button" class="text-xs text-[#35617f] font-semibold hover:underline" :disabled="!newNeed" @click="addNewNeed">Add</button>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <label class="block"><span class="text-xs text-gray-500 font-medium">Owner</span>
                    <select v-model="testForm.owner" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 bg-white text-sm">
                      <option :value="null">nobody</option>
                      <option v-for="o in owners" :key="o.id" :value="o.id">{{ o.name }}</option>
                    </select>
                  </label>
                  <label class="block"><span class="text-xs text-gray-500 font-medium">Result</span>
                    <select v-model="testForm.status" class="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 bg-white text-sm">
                      <option value="unknown">unknown</option>
                      <option value="pass">pass</option>
                      <option value="fail">fail</option>
                    </select>
                  </label>
                </div>
                <p v-if="formError" class="text-sm text-red-600">{{ formError }}</p>
                <div class="flex items-center gap-3 pt-2">
                  <button type="button" class="px-4 py-1.5 rounded bg-[#35617f] text-white text-xs font-medium hover:bg-[#2a4d65]" :disabled="saving" @click="saveTest">{{ saving ? 'Saving…' : 'Save' }}</button>
                  <button type="button" class="text-xs text-gray-600 hover:underline" @click="closeTestForm">Cancel</button>
                </div>
              </div>
            </div>

            <p v-if="!moduleOwnTests.length" class="mt-4 text-[13.5px] text-[#a8a49b] italic">No checks yet. What must be true for this module to work?</p>

            <div v-else class="divide-y divide-[#eceae4]">
              <div v-for="c in sortedCheckRows" :key="c.id" class="py-0">
                <div
                  class="flex items-center gap-3 py-3 px-1 cursor-pointer hover:bg-[#faf9f6] rounded"
                  @click="toggleCheck(c.id)"
                >
                  <span class="w-2 h-2 rounded-full shrink-0" :style="{ background: c.meta.color }"></span>
                  <span class="flex-1 min-w-0 text-[14px] leading-snug text-[#26241f]">{{ c.phrase }}</span>
                  <span
                    class="shrink-0 text-[10px] font-medium rounded-full px-2 py-0.5"
                    :style="{ background: c.meta.bg, color: c.meta.ink }"
                  >{{ c.meta.word }}</span>
                  <span class="shrink-0 w-32 text-right text-[11.5px] text-[#a8a49b] truncate">{{ c.ownerAgo }}</span>
                  <span
                    class="shrink-0 w-4 text-center text-[#c4bfb5] text-[9px] transition-transform"
                    :class="{ 'rotate-90': expandedCheckId === c.id }"
                  >▶</span>
                </div>

                <!-- Expanded Check Detail Pane -->
                <div v-if="expandedCheckId === c.id" class="pl-7 pr-1 pt-1 pb-5">
                  <div class="flex items-center gap-2.5 mb-3">
                    <button
                      type="button"
                      class="text-xs font-semibold text-white bg-[#35617f] rounded-md h-[29px] px-3 hover:bg-[#2a4d65]"
                      :disabled="checking.has(c.id)"
                      @click="runTests([c])"
                    >{{ checking.has(c.id) ? 'Checking…' : 'Re-check now' }}</button>
                    <button
                      type="button"
                      class="text-xs font-semibold text-[#1a1918] bg-white border border-[#e0ddd6] rounded-md h-[29px] px-3 hover:bg-gray-50"
                      @click="startEditTest(c)"
                    >Edit</button>
                    <button
                      type="button"
                      class="text-xs text-gray-400 hover:text-red-600 ml-auto"
                      @click="removeTest(c)"
                    >Delete check</button>
                  </div>

                  <div class="text-[12.5px] font-semibold text-[#8b877f] mb-1.5">Latest evidence</div>
                  <div v-if="testEvidence(c.id)" class="text-[13.5px] leading-relaxed text-[#38352f] whitespace-pre-wrap bg-[#faf9f6] border border-[#eceae4] rounded-md p-3">
                    {{ testEvidence(c.id).body }}
                  </div>
                  <div v-if="testEvidence(c.id)" class="text-[11.5px] text-[#a8a49b] mt-1.5">
                    {{ testEvidence(c.id).author }} · {{ ago(testEvidence(c.id).created_at) }}
                  </div>
                  <p v-else class="m-0 text-[12.5px] text-[#a8a49b] italic">Never checked.</p>

                  <div v-if="c.needs && c.needs.length" class="mt-4">
                    <div class="text-[12.5px] font-semibold text-[#8b877f] mb-1.5">True only if</div>
                    <div class="flex flex-col gap-1.5">
                      <div
                        v-for="nid in c.needs"
                        :key="nid.id || nid"
                        class="flex items-center gap-2 text-[13px] text-[#38352f] cursor-pointer hover:underline"
                        @click="goToPrereq(nid)"
                      >
                        <span class="w-[7px] h-[7px] rounded-full shrink-0" :style="{ background: prereqMeta(nid).color }"></span>
                        <span class="flex-1 min-w-0">{{ prereqPhrase(nid) }}</span>
                        <span class="text-[11px] text-[#a8a49b]">{{ prereqModuleTitle(nid) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab: History -->
          <div v-else-if="tab === 'history'" class="pt-6">
            <div v-if="historyForm" class="mb-4 p-4 border border-[#e0ddd6] bg-[#faf9f6] rounded-lg space-y-3">
              <div class="grid grid-cols-[9rem_1fr] gap-2">
                <input v-model="historyForm.date" type="date" class="border border-gray-300 rounded px-2 py-1 text-sm" />
                <input v-model="historyForm.title" type="text" placeholder="Headline" class="border border-gray-300 rounded px-2 py-1 text-sm" />
              </div>
              <textarea v-model="historyForm.body" rows="4" placeholder="What happened, why, what changed" class="w-full border border-gray-300 rounded p-2 text-sm" />
              <p v-if="formError" class="text-sm text-red-600">{{ formError }}</p>
              <div class="flex items-center gap-3">
                <button type="button" class="px-4 py-1.5 rounded bg-[#35617f] text-white text-xs font-medium hover:bg-[#2a4d65]" :disabled="saving" @click="saveHistory">{{ saving ? 'Saving…' : 'Add entry' }}</button>
                <button type="button" class="text-xs text-gray-600 hover:underline" @click="historyForm = null">Cancel</button>
              </div>
            </div>
            <div v-else class="flex justify-end mb-3">
              <button type="button" class="text-[12.5px] font-semibold text-[#35617f] hover:underline" @click="historyForm = { date: today(), title: '', body: '' }">+ Entry</button>
            </div>

            <p v-if="!detail.history.length" class="m-0 text-[13.5px] text-[#a8a49b] italic">No history recorded.</p>
            <div v-else class="divide-y divide-[#eceae4]">
              <div v-for="h in detail.history" :key="h.id" class="flex gap-4 py-3">
                <span class="shrink-0 w-24 text-[12px] text-[#a8a49b] pt-0.5">{{ day(h.created_at) }}</span>
                <div class="flex-1 min-w-0">
                  <div class="text-[14px] font-medium text-[#26241f]">{{ h.title }}</div>
                  <div v-if="h.body" class="text-[13px] text-[#6b675f] leading-relaxed mt-1 whitespace-pre-wrap">{{ h.body }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab: Code -->
          <div v-else-if="tab === 'code'" class="pt-6">
            <div v-if="codeForm" class="p-4 border border-[#e0ddd6] bg-[#faf9f6] rounded-lg space-y-3">
              <label class="block"><span class="text-xs text-gray-500 font-medium">Repos (comma separated)</span><input v-model="codeForm.repos" type="text" class="mt-1 w-full border border-gray-300 rounded px-2 py-1 font-mono text-xs" /></label>
              <label class="block"><span class="text-xs text-gray-500 font-medium">Paths (repo glob, one per line)</span><textarea v-model="codeForm.paths" rows="8" class="mt-1 w-full border border-gray-300 rounded p-2 font-mono text-xs" /></label>
              <p v-if="formError" class="text-sm text-red-600">{{ formError }}</p>
              <div class="flex items-center gap-3">
                <button type="button" class="px-4 py-1.5 rounded bg-[#35617f] text-white text-xs font-medium hover:bg-[#2a4d65]" :disabled="saving" @click="saveCode">{{ saving ? 'Saving…' : 'Save' }}</button>
                <button type="button" class="text-xs text-gray-600 hover:underline" @click="codeForm = null">Cancel</button>
              </div>
            </div>
            <div v-else>
              <div class="flex justify-end mb-3">
                <button type="button" class="text-[12.5px] font-semibold text-[#35617f] hover:underline" @click="startCode">{{ detail.paths && detail.paths.length ? 'Edit Paths' : '+ Add Paths' }}</button>
              </div>
              <p v-if="!detail.paths || !detail.paths.length" class="m-0 text-[13.5px] text-[#a8a49b] italic">No code paths linked to this module.</p>
              <div v-else class="space-y-2">
                <div v-for="(p, idx) in detail.paths" :key="idx" class="text-[13.5px] text-[#38352f] break-all leading-relaxed p-2.5 px-3 bg-[#faf9f6] border border-[#eceae4] rounded-md font-mono text-xs">
                  <span class="text-[#a8a49b]">{{ p.repo }} /</span> {{ p.glob }}
                </div>
              </div>
            </div>
          </div>

          <!-- Tab: Related / Links -->
          <div v-else-if="tab === 'related'" class="pt-6">
            <div class="mb-4 flex items-center gap-2">
              <select v-model="edgeForm.kind" class="border border-gray-300 rounded px-2.5 py-1.5 bg-white text-xs">
                <option value="depends_on">depends on</option>
                <option value="see_also">see also</option>
              </select>
              <input v-model="edgeForm.to_path" type="text" placeholder="module path e.g. features/worklist" class="flex-1 border border-gray-300 rounded px-2.5 py-1.5 text-xs" />
              <button type="button" class="text-xs px-3 py-1.5 rounded bg-[#35617f] text-white font-medium hover:bg-[#2a4d65]" :disabled="saving || !edgeForm.to_path" @click="addEdge">Link</button>
            </div>
            <p v-if="formError" class="text-sm text-red-600 mb-3">{{ formError }}</p>

            <p v-if="!allLinks.length" class="m-0 text-[13.5px] text-[#a8a49b] italic">No related modules.</p>
            <div v-else class="divide-y divide-[#eceae4]">
              <div v-for="l in allLinks" :key="l.path + l.kind" class="flex items-baseline gap-3 py-3">
                <span class="shrink-0 w-28 text-[12px] text-[#a8a49b]">{{ l.kind }}</span>
                <a class="text-[14px] font-medium text-[#35617f] cursor-pointer hover:underline flex-1" @click="selectModule(l.path)">{{ l.title }}</a>
                <button v-if="l.out" type="button" class="text-gray-300 hover:text-red-600 text-xs" @click="removeEdge(l)">✕</button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="p-12 text-center text-sm text-[#8b877f]">
          Select a module from the index.
        </div>
      </main>
    </div>

    <!-- Toast Notification -->
    <div v-if="notice" class="fixed left-1/2 bottom-6 -translate-x-1/2 bg-[#1a1918] text-[#f6f5f3] text-[12.5px] font-medium px-4 py-2.5 rounded-lg shadow-xl z-50">
      {{ notice }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

const NODES = '/api/library-nodes/';
const TESTS = '/api/library-tests/';
const RUNS = '/api/library-check-runs/';

const C = {
  pass: '#2f8f5f', passInk: '#1f6a45', passBg: '#e7f2ea',
  fail: '#c23a28', failInk: '#993022', failBg: '#f8e8e4',
  warn: '#b07d1a', warnInk: '#87611a', warnBg: '#f5edda',
  unk: '#64748b', unkInk: '#334155', unkBg: '#f1f5f9',
  none: '#dedad2', noneInk: '#8b877f', noneBg: '#f6f5f3',
};

const nodeUrl = (path, sub = '') => `${NODES}${path.replace(/^\/+|\/+$/g, '')}/${sub}`;
const libraryDetailPath = (path) => `/support/library/${path
  .split('/')
  .map((part) => encodeURIComponent(part))
  .join('/')}`;
const libraryTabPath = (path, tab) => tab === 'overview'
  ? libraryDetailPath(path)
  : `${libraryDetailPath(path)}/${tab}`;

export default {
  name: 'Support_Library',
  props: {
    libraryPath: { type: String, default: null },
    libraryTab: { type: String, default: null },
  },
  emits: ['title-change'],
  data() {
    return {
      nodes: [],
      tests: [],
      owners: [],
      loading: false,
      error: null,
      query: '',
      // 'all' | 'customer' | 'internal' — which side of the customer boundary
      // the tree shows. Not persisted: it is a lens you pick per visit.
      facing: 'all',
      expanded: {},
      selectedPath: null,
      detail: null,
      detailLoading: false,
      detailRequest: 0,
      tab: 'overview',
      expandedCheckId: null,
      checking: new Set(),
      pollTimer: null,
      runs: [],
      runsOpen: false,
      runPollTimer: null,
      expandedRunId: null,
      C,
      notice: '',
      historyRows: {},
      sidebarWidth: 360,
      sidebarCollapsed: false,
      isDragging: false,

      // Forms
      moduleForm: null,
      detailsForm: null,
      bodyForm: null,
      historyForm: null,
      codeForm: null,
      edgeForm: { kind: 'depends_on', to_path: '' },
      testForm: null,
      newNeed: '',
      formError: null,
      saving: false,
      confirming: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    byId() {
      return Object.fromEntries(this.tests.map((t) => [t.id, t]));
    },
    activeRun() {
      return this.runs.find((r) => r.status === 'queued' || r.status === 'running') || null;
    },
    lastRun() {
      return this.runs.find((r) => r.status === 'done' || r.status === 'failed') || null;
    },
    liveTestState() {
      const out = {};
      for (const r of this.runs) {
        if (r.status !== 'queued' && r.status !== 'running') continue;
        for (const t of r.tests) {
          if (t.state === 'checking' || t.state === 'queued') out[t.id] = t.state;
        }
      }
      return out;
    },
    byPath() {
      return Object.fromEntries(this.nodes.map((n) => [n.path, n]));
    },
    // Counts are over the whole library, so they answer "how much of what we
    // document is customer-facing" and do not shift as you type a search.
    facingFilters() {
      const cf = this.nodes.filter((n) => n.tenant_facing).length;
      return [
        { key: 'all', label: 'All', count: this.nodes.length, hint: 'Every module' },
        { key: 'customer', label: 'Customer', count: cf, hint: 'Documents product a customer touches' },
        { key: 'internal', label: 'Internal', count: this.nodes.length - cf, hint: 'Internal machinery — no customer surface' },
      ];
    },
    filteredNodes() {
      const q = this.query.toLowerCase().trim();
      return this.nodes.filter((n) => this.matchesFacing(n) && (!q || (
        n.path.toLowerCase().includes(q) ||
        n.title.toLowerCase().includes(q) ||
        (n.summary || '').toLowerCase().includes(q)
      )));
    },
    tree() {
      const paths = this.nodes.map((n) => n.path);
      const q = this.query.toLowerCase().trim();
      let keep = new Set(paths);
      // A row survives when it matches BOTH the search and the customer/internal
      // lens. An ancestor of a surviving row is kept even if it does not match
      // itself — otherwise a customer-facing leaf under an internal parent
      // becomes unreachable, since the tree only renders paths whose ancestors
      // are present.
      if (q || this.facing !== 'all') {
        keep = new Set();
        for (const p of paths) {
          const n = this.byPath[p];
          if (!n) continue;
          const hit = !q || p.toLowerCase().includes(q) || n.title.toLowerCase().includes(q) || (n.summary || '').toLowerCase().includes(q);
          if (hit && this.matchesFacing(n)) {
            const segs = p.split('/');
            for (let i = 1; i <= segs.length; i += 1) keep.add(segs.slice(0, i).join('/'));
          }
        }
      }
      const depth = (p) => p.split('/').length - 1;
      const parentOf = (p) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : null);
      const hasChildren = (p) => paths.some((x) => parentOf(x) === p);
      const isAncestorClosed = (p) => {
        let a = parentOf(p);
        while (a) {
          if (!this.isExpanded(a)) return true;
          a = parentOf(a);
        }
        return false;
      };

      const ordered = paths.filter((p) => keep.has(p)).sort((a, b) => {
        const A = a.split('/');
        const B = b.split('/');
        for (let i = 0; i < Math.min(A.length, B.length); i += 1) {
          if (A[i] !== B[i]) {
            const titleA = this.byPath[A.slice(0, i + 1).join('/')]?.title || '';
            const titleB = this.byPath[B.slice(0, i + 1).join('/')]?.title || '';
            return titleA.localeCompare(titleB);
          }
        }
        return A.length - B.length;
      });

      return ordered.filter((p) => !isAncestorClosed(p)).map((p) => {
        const n = this.byPath[p] || { title: p };
        const ownTests = this.tests.filter((t) => t.module === p);
        return {
          path: p,
          title: n.title,
          depth: depth(p),
          hasChildren: hasChildren(p),
          aggColor: this.aggColor(p),
          tenantFacing: !!n.tenant_facing,
          testsCount: ownTests.length ? ownTests.length : null,
        };
      });
    },
    breadcrumbs() {
      if (!this.selectedPath) return [];
      const segs = this.selectedPath.split('/');
      return segs.map((_, i) => {
        const p = segs.slice(0, i + 1).join('/');
        return { path: p, title: this.byPath[p]?.title || p };
      });
    },
    detailOwnerName() {
      if (!this.detail?.owner) return 'nobody';
      const o = this.owners.find((u) => u.id === this.detail.owner);
      return o ? o.name : 'staff';
    },
    moduleDeepTests() {
      if (!this.selectedPath) return [];
      return this.tests.filter((t) => t.module === this.selectedPath || t.module.startsWith(`${this.selectedPath}/`));
    },
    moduleOwnTests() {
      if (!this.selectedPath) return [];
      return this.tests.filter((t) => t.module === this.selectedPath);
    },
    sortedCheckRows() {
      return this.moduleOwnTests.map((c) => ({
        ...c,
        meta: this.metaFor(c),
        ownerAgo: `${c.owner_name ? c.owner_name.split(' ')[0] : 'unowned'} · ${this.ago(c.checked_at)}`,
      })).sort((a, b) => {
        const rank = (x) => (x.status === 'fail' ? 0 : (x.effective_status === 'fail' ? 1 : (x.effective_status === 'unknown' ? 2 : 3)));
        return rank(a) - rank(b);
      });
    },
    moduleChecking() {
      return this.moduleDeepTests.some((t) => this.checking.has(t.id));
    },
    moduleHealth() {
      const ts = this.moduleDeepTests;
      if (!ts.length) return { score: '—', label: 'no tests', color: C.none };
      const passed = ts.filter((t) => t.effective_status === 'pass').length;
      const fails = ts.filter((t) => t.effective_status === 'fail').length;
      const unk = ts.filter((t) => t.effective_status === 'unknown').length;
      let color = C.pass;
      if (fails > 0) color = C.fail;
      else if (unk > 0) color = C.unk;
      return { score: `${passed}/${ts.length}`, label: 'tests passed', color };
    },
    lastTestedLabel() {
      const checked = this.moduleDeepTests.map((t) => t.checked_at).filter(Boolean).sort();
      return checked.length ? this.ago(checked[checked.length - 1]) : 'never';
    },
    checkSummaryText() {
      const own = this.moduleOwnTests;
      if (!own.length) return 'none here';
      const passed = own.filter((t) => t.effective_status === 'pass').length;
      return `${passed}/${own.length} passed here`;
    },
    // THREE HEADING CONVENTIONS EXIST IN THE BODIES, and the old parser knew
    // only one of them ("=== HEADING ==="), which 2 modules use. 203 use a bare
    // uppercase line, most of those fenced by a rule line above and below:
    //
    //     ================================
    //     THE FOUR TICK DUTIES
    //     ================================
    //
    // Every one of those rendered as a single blob with its headings swallowed
    // into the paragraph text, because the rule lines were stripped and the
    // title between them was treated as prose. All three are read here.
    // Paragraphs are split on blank lines so leading/trailing whitespace inside
    // a section cannot collapse two paragraphs into one wall of text, and
    // bullet runs are kept as lists rather than being justified as prose.
    sections() {
      const text = this.detail?.body || '';
      const lines = text.split('\n');
      const isRule = (l) => /^[=-]{4,}\s*$/.test(l);
      // A heading is short, has no sentence punctuation, and is not lower-case
      // prose. Requiring at least one letter keeps a stray "1." from matching.
      const isBareHeading = (l) => {
        const t = l.trim();
        if (!t || t.length > 90) return false;
        if (/[.;:,?]$/.test(t)) return false;
        if (!/[A-Z]/.test(t)) return false;
        return t === t.toUpperCase();
      };

      const out = [];
      let current = { heading: '', blocks: [] };
      let buffer = [];
      const flushParagraph = () => {
        const joined = buffer.join('\n').trim();
        buffer = [];
        if (!joined) return;
        const rows = joined.split('\n');
        const bullets = rows.every((r) => /^\s*[-*•]\s+/.test(r));
        if (bullets) {
          current.blocks.push({ type: 'list', items: rows.map((r) => r.replace(/^\s*[-*•]\s+/, '').trim()) });
        } else {
          current.blocks.push({ type: 'p', text: joined });
        }
      };
      const flushSection = () => {
        flushParagraph();
        if (current.blocks.length || current.heading) out.push(current);
      };

      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        // ORDER MATTERS. A rule line is 80 '=' characters, and the old inline
        // pattern /^={3,}\s*(.+?)\s*={3,}$/ matched one against ITSELF: the
        // lazy (.+?) captured a single '=', so every fenced section rendered a
        // stray "=" heading and pushed its real title down into the prose.
        // That was the formatting corruption. Rule lines are classified first,
        // and the inline title must contain something that is not '='.
        const fenced = isRule(line) && isBareHeading(lines[i + 1] || '');
        const inline = fenced ? null : line.match(/^={3,}\s*([^=].*?)\s*={3,}$/);
        if (inline || fenced) {
          flushSection();
          current = { heading: inline ? inline[1] : lines[i + 1].trim(), blocks: [] };
          if (fenced) {
            i += 1;
            if (isRule(lines[i + 1] || '')) i += 1;
          }
          continue;
        }
        if (isRule(line)) continue;
        if (isBareHeading(line)) {
          flushSection();
          current = { heading: line.trim(), blocks: [] };
          continue;
        }
        if (!line.trim()) {
          flushParagraph();
          continue;
        }
        buffer.push(line);
      }
      flushSection();
      return out;
    },
    tabList() {
      return [
        { key: 'overview', label: 'Overview', count: 0 },
        { key: 'checks', label: 'Checks', count: this.moduleOwnTests.length },
        { key: 'history', label: 'History', count: this.detail?.history?.length || 0 },
        { key: 'code', label: 'Code', count: this.detail?.paths?.length || 0 },
        { key: 'related', label: 'Related', count: (this.detail?.edges_out?.length || 0) + (this.detail?.edges_in?.length || 0) },
      ];
    },
    allLinks() {
      const d = this.detail;
      if (!d) return [];
      const out = (d.edges_out || []).map((e) => ({ ...e, out: true }));
      const inc = (d.edges_in || []).map((e) => ({ ...e, out: false }));
      return [...out, ...inc];
    },
  },
  watch: {
    libraryPath: {
      immediate: true,
      handler(path) {
        this.syncSelectedPath(path);
      },
    },
    libraryTab: {
      immediate: true,
      handler(tab) {
        this.tab = tab || 'overview';
      },
    },
    selectedPath() {
      this.resetForms();
      this.expandedCheckId = null;
      this.tab = this.libraryTab || 'overview';
      this.$emit('title-change', '');
      this.loadDetail(false);
    },
  },
  created() {
    const savedWidth = localStorage.getItem('library_sidebar_width');
    if (savedWidth) {
      const parsed = parseInt(savedWidth, 10);
      if (!Number.isNaN(parsed) && parsed >= 220 && parsed <= 900) {
        this.sidebarWidth = parsed;
      }
    }
    this.sidebarCollapsed = localStorage.getItem('library_sidebar_collapsed') === 'true';
    const savedCollapsed = localStorage.getItem('library_tree_expanded');
    if (savedCollapsed) {
      try {
        this.expanded = JSON.parse(savedCollapsed);
      } catch {
        this.expanded = {};
      }
    }
    this.load();
    this.loadOwners();
    this.loadRuns();
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.runPollTimer) clearInterval(this.runPollTimer);
    window.removeEventListener('mousemove', this.onResize);
    window.removeEventListener('mouseup', this.stopResize);
  },
  methods: {
    toast(m) {
      this.notice = m;
      clearTimeout(this._t);
      this._t = setTimeout(() => { this.notice = ''; }, 2500);
    },
    startResize(e) {
      this.isDragging = true;
      this.startX = e.clientX;
      this.startWidth = this.sidebarWidth;
      window.addEventListener('mousemove', this.onResize);
      window.addEventListener('mouseup', this.stopResize);
    },
    onResize(e) {
      if (!this.isDragging) return;
      const delta = e.clientX - this.startX;
      const newWidth = Math.max(220, Math.min(window.innerWidth - 400, this.startWidth + delta));
      this.sidebarWidth = newWidth;
    },
    stopResize() {
      if (!this.isDragging) return;
      this.isDragging = false;
      localStorage.setItem('library_sidebar_width', String(this.sidebarWidth));
      window.removeEventListener('mousemove', this.onResize);
      window.removeEventListener('mouseup', this.stopResize);
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      localStorage.setItem('library_sidebar_collapsed', String(this.sidebarCollapsed));
    },
    isExpanded(path) {
      return !!this.expanded[path];
    },
    toggleExpand(path) {
      this.expanded[path] = !this.isExpanded(path);
      localStorage.setItem('library_tree_expanded', JSON.stringify(this.expanded));
    },
    expandAncestors(path) {
      const segs = path.split('/');
      for (let i = 1; i < segs.length; i += 1) {
        this.expanded[segs.slice(0, i).join('/')] = true;
      }
      localStorage.setItem('library_tree_expanded', JSON.stringify(this.expanded));
    },
    syncSelectedPath(path) {
      // The base Library URL remains the main library. It displays the first
      // module until the user chooses an addressable module page.
      const nextPath = path || this.nodes[0]?.path;
      if (!nextPath) return;
      if (this.selectedPath !== nextPath) {
        this.selectedPath = nextPath;
        this.expandAncestors(nextPath);
      }
    },
    navigateToModule(path, tab = 'overview') {
      const isCurrentPage = this.libraryPath === path
        && (tab === 'overview' ? !this.libraryTab : this.libraryTab === tab);
      if (isCurrentPage) return;
      this.$router?.push({
        path: libraryTabPath(path, tab),
        query: this.$route.query,
        hash: this.$route.hash,
      });
    },
    selectTab(tab) {
      if (!this.selectedPath) return;
      if (tab === 'overview' && !this.libraryTab) return;
      if (this.libraryTab === tab) return;
      this.$router?.push({
        path: libraryTabPath(this.selectedPath, tab),
        query: this.$route.query,
        hash: this.$route.hash,
      });
    },
    selectModule(path, tab = 'overview') {
      this.resetForms();
      if (this.selectedPath !== path) {
        this.selectedPath = path;
        this.expandAncestors(path);
      }
      this.navigateToModule(path, tab);
    },
    moduleTitle(path) {
      return this.byPath[path]?.title || path;
    },
    aggColor(path) {
      const ts = this.tests.filter((t) => t.module === path || t.module.startsWith(`${path}/`));
      if (!ts.length) return '#dedad2';
      if (ts.some((t) => t.effective_status === 'fail')) return C.fail;
      if (ts.some((t) => t.effective_status === 'unknown')) return C.unk;
      return C.pass;
    },
    metaFor(c) {
      const live = this.liveTestState[c.id];
      if (live === 'checking') {
        return { color: C.warn, ink: C.warnInk, bg: C.warnBg, word: 'checking' };
      }
      if (live === 'queued' || this.checking.has(c.id)) {
        return { color: C.warn, ink: C.warnInk, bg: C.warnBg, word: 'queued' };
      }
      if (c.effective_status === 'pass') {
        return { color: C.pass, ink: C.passInk, bg: C.passBg, word: 'passed' };
      }
      if (c.effective_status === 'fail') {
        return c.status === 'fail'
          ? { color: C.fail, ink: C.failInk, bg: C.failBg, word: 'root cause' }
          : { color: C.fail, ink: C.failInk, bg: C.failBg, word: 'blocked' };
      }
      return { color: C.unk, ink: C.unkInk, bg: C.unkBg, word: 'unchecked' };
    },
    ago(iso) {
      if (!iso) return 'never';
      const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
      if (mins < 60) return `${mins}m ago`;
      if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
      return `${Math.round(mins / 1440)}d ago`;
    },
    day(iso) {
      return iso ? iso.slice(0, 10) : '';
    },
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    resetForms() {
      this.moduleForm = null;
      this.detailsForm = null;
      this.bodyForm = null;
      this.historyForm = null;
      this.codeForm = null;
      this.testForm = null;
      this.formError = null;
      this.confirming = false;
    },
    toggleCheck(id) {
      this.expandedCheckId = this.expandedCheckId === id ? null : id;
      if (this.expandedCheckId && !this.historyRows[id]) {
        this.loadCheckHistory(id);
      }
    },
    testEvidence(testId) {
      const rows = this.historyRows[testId];
      return rows && rows.length ? rows[0] : null;
    },
    prereqMeta(nid) {
      const id = nid.id || nid;
      const t = this.byId[id];
      if (!t) return { color: C.unk };
      return this.metaFor(t);
    },
    prereqPhrase(nid) {
      const id = nid.id || nid;
      return this.byId[id]?.phrase || String(id);
    },
    prereqModuleTitle(nid) {
      const id = nid.id || nid;
      const m = this.byId[id]?.module;
      return m ? this.moduleTitle(m) : '';
    },
    goToPrereq(nid) {
      const id = nid.id || nid;
      const t = this.byId[id];
      if (t) {
        this.selectModule(t.module, 'checks');
        this.tab = 'checks';
        this.expandedCheckId = t.id;
      }
    },
    async load() {
      this.loading = true;
      try {
        const [nodes, tests] = await Promise.all([axios.get(NODES), axios.get(TESTS)]);
        this.nodes = nodes.data;
        this.tests = tests.data;
        const hadSelectedPath = !!this.selectedPath;
        this.syncSelectedPath(this.libraryPath);
        // A direct route selects its path before this initial list request
        // finishes, before the selectedPath watcher is registered. Load that
        // selected module explicitly so the detail pane is not left empty.
        if (hadSelectedPath && this.selectedPath) this.loadDetail(true);
      } catch (e) {
        this.error = e;
      } finally {
        this.loading = false;
      }
    },
    async loadOwners() {
      try {
        const { data } = await axios.get(`${TESTS}owners/`);
        this.owners = data;
      } catch {
        this.owners = [];
      }
    },
    async loadDetail(keepTab) {
      const path = this.selectedPath;
      if (!path) return;
      const request = ++this.detailRequest;
      if (!keepTab) this.detail = null;
      this.detailLoading = true;
      try {
        const { data } = await axios.get(nodeUrl(path));
        if (request === this.detailRequest) {
          this.detail = data;
          this.$emit('title-change', data.title);
        }
      } catch (e) {
        if (request === this.detailRequest) {
          this.detail = null;
          this.$emit('title-change', '');
          if (e?.response?.status === 404) {
            const parent = this.nearestExistingAncestor(path);
            this.toast(`"${path}" no longer exists.`);
            if (parent) this.selectModule(parent);
          }
        }
      } finally {
        if (request === this.detailRequest) this.detailLoading = false;
      }
    },
    nearestExistingAncestor(path) {
      const segs = path.split('/');
      for (let i = segs.length - 1; i > 0; i -= 1) {
        const candidate = segs.slice(0, i).join('/');
        if (this.byPath[candidate]) return candidate;
      }
      return this.nodes[0]?.path || null;
    },
    async loadCheckHistory(testId) {
      try {
        const { data } = await axios.get(`${TESTS}${testId}/history/`);
        this.historyRows[testId] = data;
      } catch {
        this.historyRows[testId] = [];
      }
    },
    startModule(parent = '') {
      this.resetForms();
      this.moduleForm = { parent, title: '', summary: '', when_to_read: '' };
    },
    async saveModule() {
      const f = this.moduleForm;
      this.saving = true;
      this.formError = null;
      try {
        const { data } = await axios.post(NODES, {
          parent_path: f.parent || null,
          title: f.title,
          summary: f.summary,
          when_to_read: f.when_to_read,
        });
        this.moduleForm = null;
        await this.load();
        this.selectModule(data.path);
        this.toast('Module created.');
      } catch (e) {
        this.formError = e?.response?.data ? JSON.stringify(e.response.data) : 'Could not create module.';
      } finally {
        this.saving = false;
      }
    },
    matchesFacing(n) {
      if (this.facing === 'customer') return !!n.tenant_facing;
      if (this.facing === 'internal') return !n.tenant_facing;
      return true;
    },
    startDetails() {
      this.resetForms();
      const parent = this.selectedPath.includes('/') ? this.selectedPath.slice(0, this.selectedPath.lastIndexOf('/')) : '';
      this.detailsForm = {
        title: this.detail.title,
        summary: this.detail.summary || '',
        when_to_read: this.detail.when_to_read || '',
        status: this.detail.status,
        tenant_facing: !!this.detail.tenant_facing,
        parent_path: parent,
      };
    },
    async saveDetails() {
      const f = this.detailsForm;
      this.saving = true;
      this.formError = null;
      try {
        const { data } = await axios.patch(nodeUrl(this.selectedPath), {
          title: f.title,
          summary: f.summary,
          when_to_read: f.when_to_read,
          status: f.status,
          tenant_facing: f.tenant_facing,
          parent_path: f.parent_path || null,
        });
        this.detailsForm = null;
        await this.load();
        this.selectModule(data.path);
        this.toast('Module updated.');
      } catch (e) {
        this.formError = e?.response?.data ? JSON.stringify(e.response.data) : 'Could not save module.';
      } finally {
        this.saving = false;
      }
    },
    async removeModule() {
      if (!this.confirming) {
        this.confirming = true;
        setTimeout(() => { this.confirming = false; }, 3000);
        return;
      }
      this.confirming = false;
      const path = this.selectedPath;
      try {
        await axios.delete(nodeUrl(path));
        this.selectedPath = null;
        await this.load();
        this.toast('Module deleted.');
      } catch {
        this.toast('Could not delete module.');
      }
    },
    startBody() {
      this.formError = null;
      this.bodyForm = { body: this.detail.body || '', note: '' };
    },
    async saveBody() {
      const f = this.bodyForm;
      this.saving = true;
      this.formError = null;
      try {
        await axios.patch(nodeUrl(this.selectedPath), { body: f.body, note: f.note });
        this.bodyForm = null;
        await this.loadDetail(true);
        this.toast('Body updated.');
      } catch (e) {
        this.formError = e?.response?.data ? JSON.stringify(e.response.data) : 'Could not save body.';
      } finally {
        this.saving = false;
      }
    },
    async saveHistory() {
      const f = this.historyForm;
      this.saving = true;
      this.formError = null;
      try {
        const { data } = await axios.post(nodeUrl(this.selectedPath, 'history/'), {
          date: f.date ? `${f.date}T12:00:00Z` : null,
          title: f.title,
          body: f.body,
        });
        this.historyForm = null;
        this.detail = data;
        this.toast('History entry added.');
      } catch (e) {
        this.formError = e?.response?.data ? JSON.stringify(e.response.data) : 'Could not add history.';
      } finally {
        this.saving = false;
      }
    },
    startCode() {
      this.formError = null;
      this.codeForm = {
        repos: (this.detail.repos || []).join(', '),
        paths: (this.detail.paths || []).map((p) => `${p.repo} ${p.glob}`).join('\n'),
      };
    },
    async saveCode() {
      const f = this.codeForm;
      const repos = f.repos.split(',').map((s) => s.trim()).filter(Boolean);
      const paths = f.paths.split('\n').map((s) => s.trim()).filter(Boolean).map((line) => {
        const [repo, ...rest] = line.split(/\s+/);
        return { repo, glob: rest.join(' ') };
      });
      this.saving = true;
      this.formError = null;
      try {
        await axios.patch(nodeUrl(this.selectedPath), { repos, paths, note: 'code paths updated' });
        this.codeForm = null;
        await this.loadDetail(true);
        this.toast('Code paths saved.');
      } catch (e) {
        this.formError = e?.response?.data ? JSON.stringify(e.response.data) : 'Could not save code paths.';
      } finally {
        this.saving = false;
      }
    },
    async addEdge() {
      const f = this.edgeForm;
      this.saving = true;
      this.formError = null;
      try {
        const { data } = await axios.post(nodeUrl(this.selectedPath, 'edges/'), { kind: f.kind, to_path: f.to_path });
        this.edgeForm.to_path = '';
        this.detail = data;
        this.toast('Link added.');
      } catch (e) {
        this.formError = e?.response?.data ? JSON.stringify(e.response.data) : 'Could not add link.';
      } finally {
        this.saving = false;
      }
    },
    async removeEdge(e) {
      try {
        const { data } = await axios.delete(nodeUrl(this.selectedPath, 'edges/'), { data: { kind: e.kind, to_path: e.path } });
        this.detail = data;
        this.toast('Link removed.');
      } catch {
        this.toast('Could not remove link.');
      }
    },
    startCreateTest(modulePath) {
      this.resetForms();
      this.newNeed = '';
      this.testForm = { id: null, phrase: '', module: modulePath, owner: null, needs: [], status: 'unknown' };
    },
    startEditTest(t) {
      this.resetForms();
      this.newNeed = '';
      this.testForm = {
        id: t.id,
        phrase: t.phrase,
        module: t.module,
        owner: t.owner,
        needs: t.needs || [],
        status: t.status,
      };
    },
    addNewNeed() {
      if (!this.newNeed) return;
      this.testForm.needs.push({ phrase: this.newNeed });
      this.newNeed = '';
    },
    removeNeed(n) {
      this.testForm.needs = this.testForm.needs.filter((x) => x !== n && x.id !== n.id);
    },
    closeTestForm() {
      this.testForm = null;
      this.formError = null;
    },
    async saveTest() {
      const f = this.testForm;
      const payload = {
        phrase: f.phrase,
        module: f.module,
        owner: f.owner,
        status: f.status,
        needs: f.needs.map((n) => n.phrase || (typeof n === 'string' ? n : '')).filter(Boolean),
      };
      this.saving = true;
      this.formError = null;
      try {
        if (f.id) {
          await axios.patch(`${TESTS}${f.id}/`, payload);
        } else {
          await axios.post(TESTS, payload);
        }
        this.testForm = null;
        await this.load();
        this.toast('Check saved.');
      } catch (e) {
        this.formError = e?.response?.data ? JSON.stringify(e.response.data) : 'Could not save check.';
      } finally {
        this.saving = false;
      }
    },
    async removeTest(t) {
      try {
        await axios.delete(`${TESTS}${t.id}/`);
        this.expandedCheckId = null;
        await this.load();
        this.toast('Check removed.');
      } catch {
        this.toast('Could not delete check.');
      }
    },
    async runTests(testsToRun) {
      const ids = testsToRun.map((t) => t.id).filter((id) => !this.checking.has(id) && !this.liveTestState[id]);
      if (!ids.length) return;
      this.checking = new Set([...this.checking, ...ids]);
      try {
        await axios.post(`${TESTS}run/`, { ids });
        this.toast(`Re-checking ${ids.length} test(s)…`);
        await this.loadRuns();
      } catch {
        this.checking = new Set([...this.checking].filter((id) => !ids.includes(id)));
        this.toast('Could not start check.');
      }
    },
    async loadRuns() {
      let data;
      try {
        ({ data } = await axios.get(RUNS));
      } catch {
        return;
      }
      const wasLive = new Set(this.runs.filter((r) => r.status === 'queued' || r.status === 'running').map((r) => r.id));
      this.runs = data;
      const live = data.filter((r) => r.status === 'queued' || r.status === 'running');
      if (live.length) {
        this.checking = new Set([...this.checking].filter((id) => !this.liveTestState[id]));
        if (!this.runPollTimer) this.runPollTimer = setInterval(this.loadRuns, 5000);
        return;
      }
      const finished = data.filter((r) => wasLive.has(r.id));
      if (finished.length || this.checking.size) {
        this.checking = new Set();
        try {
          const { data: tests } = await axios.get(TESTS);
          this.tests = tests;
        } catch {
          // the next poll or reload refreshes it
        }
        for (const r of finished) {
          for (const id of r.test_ids) this.loadCheckHistory(id);
          this.toast(r.status === 'failed' ? `Check run failed: ${r.error || 'the checker did not report back'}` : `Checks completed: ${this.runCountsText(r)}`);
        }
      }
      if (this.runPollTimer) {
        clearInterval(this.runPollTimer);
        this.runPollTimer = null;
      }
    },
    runWho(r) {
      if (r.started_by_name) return `${r.started_by_name} (${r.source})`;
      return r.source === 'schedule' ? 'nightly schedule' : r.source;
    },
    runCountsText(r) {
      const c = r.counts || {};
      const parts = [];
      if (c.pass) parts.push(`${c.pass} pass`);
      if (c.fail) parts.push(`${c.fail} fail`);
      if (c.unknown) parts.push(`${c.unknown} unknown`);
      return parts.join(' · ');
    },
    runStatusWord(r) {
      if (r.status === 'done') return (r.counts?.fail ? 'finished with failures' : 'finished');
      if (r.status === 'failed') return 'failed';
      if (r.status === 'queued') return 'queued';
      return 'running';
    },
    runColor(r) {
      if (r.status === 'failed') return C.fail;
      if (r.status === 'done') return r.counts?.fail ? C.fail : (r.counts?.unknown ? C.unk : C.pass);
      return C.warn;
    },
    runBg(r) {
      const c = this.runColor(r);
      return c === C.fail ? C.failBg : c === C.pass ? C.passBg : c === C.warn ? C.warnBg : C.unkBg;
    },
    runInk(r) {
      const c = this.runColor(r);
      return c === C.fail ? C.failInk : c === C.pass ? C.passInk : c === C.warn ? C.warnInk : C.unkInk;
    },
    runTestColor(t) {
      if (t.result === 'pass') return C.pass;
      if (t.result === 'fail') return C.fail;
      if (t.state === 'checking') return C.warn;
      if (t.result === 'unknown') return C.unk;
      return C.none;
    },
    runTook(r) {
      const start = r.started_at || r.created_at;
      const end = r.finished_at || (r.status === 'running' || r.status === 'queued' ? null : r.updated_at);
      if (!start || !end) return r.status === 'running' || r.status === 'queued' ? '…' : '';
      const secs = Math.max(0, Math.round((new Date(end) - new Date(start)) / 1000));
      if (secs < 90) return `${secs}s`;
      return `${Math.round(secs / 60)}m`;
    },
    stamp(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      const sameDay = d.toDateString() === new Date().toDateString();
      const hm = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return sameDay ? hm : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${hm}`;
    },
  },
};
</script>

<style scoped>
.font-public-sans {
  font-family: 'Public Sans', system-ui, -apple-system, sans-serif;
}
.library-container {
  height: calc(100vh - 54px);
  display: flex;
  flex-direction: column;
  background: #f6f5f3;
  color: #1a1918;
  font-size: 13.5px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}
.run-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  padding: 4px 20px;
  background: #fbfaf8;
  border-bottom: 1px solid #eceae4;
  font-size: 12.5px;
  flex-shrink: 0;
}
.run-pulse {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #b07d1a;
  flex-shrink: 0;
  animation: run-pulse 1.4s ease-in-out infinite;
}
@keyframes run-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.75); }
}
.run-history {
  flex-shrink: 0;
  max-height: 280px;
  overflow-y: auto;
  background: #fff;
  border-bottom: 1px solid #eceae4;
}
.run-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}
.run-table th {
  position: sticky;
  top: 0;
  background: #fff;
  text-align: left;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8b877f;
  padding: 6px 20px 6px 0;
  border-bottom: 1px solid #eceae4;
}
.run-table th:first-child,
.run-table td:first-child { padding-left: 20px; }
.run-table td { padding: 6px 20px 6px 0; border-bottom: 1px solid #f1efe9; color: #26241f; vertical-align: top; }
.run-row { cursor: pointer; }
.run-row:hover td { background: #faf9f6; }
.run-row.is-open td { background: #faf9f6; border-bottom: none; }
.run-detail td { padding: 4px 20px 12px 44px; background: #faf9f6; }
.top-bar {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background: #fff;
  border-bottom: 1px solid #e4e0d9;
  flex: none;
}
.main-body {
  flex: 1;
  min-height: 0;
  display: flex;
}
.sidebar {
  flex: none;
  background: #fbfaf8;
  border-right: 1px solid #e4e0d9;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.sidebar-toggle-rail {
  width: 40px;
  flex: none;
  display: flex;
  justify-content: center;
  padding-top: 10px;
  background: #fbfaf8;
  border-right: 1px solid #e4e0d9;
}
.resizer-bar {
  width: 8px;
  margin-left: -4px;
  margin-right: -4px;
  cursor: col-resize;
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}
.resizer-bar:hover,
.resizer-bar.is-resizing {
  background: rgba(53, 97, 127, 0.12);
}
.resizer-handle {
  width: 2px;
  height: 28px;
  border-radius: 1px;
  background: #d4cfc5;
  transition: background 0.15s ease;
}
.resizer-bar:hover .resizer-handle,
.resizer-bar.is-resizing .resizer-handle {
  background: #35617f;
  height: 36px;
}
.content-pane {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  background: #fff;
}
.tree-row {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding-right: 8px;
  cursor: pointer;
  border-radius: 5px;
  transition: background 0.1s ease;
}
.tree-row:hover {
  background: #f0eee8;
}
.tree-row.selected {
  background: #eef2f5;
  box-shadow: inset 2px 0 0 #35617f;
}
.om-scroll::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}
.om-scroll::-webkit-scrollbar-thumb {
  background: #dedad2;
}
.om-scroll:hover::-webkit-scrollbar-thumb {
  background: #cfcabf;
}
.om-scroll::-webkit-scrollbar-track {
  background: transparent;
}
</style>
